import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ValidationAttributeDetailStep } from "./validationAttribute";
import { FullAsinWithAudits, Requester } from "../../server/asin-central/model";
import { AppThunk } from "../app/store";
import {
  addNotification,
  createNotificationDefinition,
} from "../notification/notificationConnector";
import {
  createAsinMarketplaceValidationDetailPassExceptions,
  publishCorrectCatalogDefaults,
} from "../app/api";
import { ERROR_NOTIF_ITEMS } from "../notification/notification-config";
import { setViewingValidationTable } from "./asinValidationTableConnector";
import logger from "../../art-logger-config";
import { CatalogDefaultValues } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";

export interface ValidationTableState {
  detailSteps: ValidationAttributeDetailStep[];
  initialDetailSteps?: ValidationAttributeDetailStep[];
  detailStepsFailedLVS: ValidationAttributeDetailStep[];
  initialDetailStepsFailedLVS?: ValidationAttributeDetailStep[];
  asin: FullAsinWithAudits | null;
  service: string;
  isOpen: boolean;
  title: string;
  waitingPassException: boolean;
  receivedPassException: boolean;
  correctCatalogDefaultsExpandedVisible: boolean;
  passExceptionExpandedVisible: boolean;
  waitingGetCorrectCatalogDefaults: boolean;
  receivedGetCorrectCatalogDefaults: boolean;
  waitingPublishCorrectCatalogDefaults: boolean;
  receivedPublishCorrectCatalogDefaults: boolean;
  catalogDefaultsValues: CatalogDefaultValues | undefined;
  catalogDefaultsValuesLookup: { [key: string]: any };
  submittedPublishCorrectCatalogDefaults: ValidationAttributeDetailStep[];
}

export const validationModalInitialState: ValidationTableState = {
  detailSteps: [],
  initialDetailSteps: [],
  detailStepsFailedLVS: [],
  initialDetailStepsFailedLVS: [],
  asin: null,
  service: "",
  isOpen: false,
  title: "Validation Results",
  waitingPassException: false,
  receivedPassException: false,
  correctCatalogDefaultsExpandedVisible: true,
  passExceptionExpandedVisible: true,
  waitingGetCorrectCatalogDefaults: false,
  receivedGetCorrectCatalogDefaults: false,
  waitingPublishCorrectCatalogDefaults: false,
  receivedPublishCorrectCatalogDefaults: false,
  catalogDefaultsValues: undefined,
  catalogDefaultsValuesLookup: {},
  submittedPublishCorrectCatalogDefaults: [],
};

const validationModalState = createSlice({
  name: "validationModalState",
  initialState: validationModalInitialState,
  reducers: {
    setValidationModalState(
      state: ValidationTableState,
      action: PayloadAction<ValidationTableState>
    ) {
      if (action.payload.isOpen) {
        state.detailSteps = action.payload.detailSteps ?? [];
        state.initialDetailSteps = action.payload.initialDetailSteps ?? [];
        state.detailStepsFailedLVS = action.payload.detailStepsFailedLVS ?? [];
        state.initialDetailStepsFailedLVS =
          action.payload.initialDetailStepsFailedLVS ?? [];
        state.isOpen = true;
        state.title = action.payload.title;
        state.asin = action.payload.asin;
        state.service = action.payload.service;
        state.waitingPassException = action.payload.waitingPassException;
        state.receivedPassException = action.payload.receivedPassException;
        state.correctCatalogDefaultsExpandedVisible =
          action.payload.correctCatalogDefaultsExpandedVisible;
        state.passExceptionExpandedVisible =
          action.payload.passExceptionExpandedVisible;
        state.waitingGetCorrectCatalogDefaults =
          action.payload.waitingGetCorrectCatalogDefaults;
        state.receivedGetCorrectCatalogDefaults =
          action.payload.receivedGetCorrectCatalogDefaults;
        state.waitingPublishCorrectCatalogDefaults =
          action.payload.waitingPublishCorrectCatalogDefaults;
        state.receivedPublishCorrectCatalogDefaults =
          action.payload.receivedPublishCorrectCatalogDefaults;
        state.catalogDefaultsValues = action.payload.catalogDefaultsValues;
        state.catalogDefaultsValuesLookup =
          action.payload.catalogDefaultsValuesLookup;
        state.submittedPublishCorrectCatalogDefaults =
          action.payload.submittedPublishCorrectCatalogDefaults;
      } else {
        state.detailSteps = validationModalInitialState.detailSteps;
        state.initialDetailSteps = validationModalInitialState.detailSteps;
        state.detailStepsFailedLVS = action.payload.detailStepsFailedLVS ?? [];
        state.initialDetailStepsFailedLVS =
          action.payload.initialDetailStepsFailedLVS ?? [];
        state.isOpen = false;
        state.title = validationModalInitialState.title;
        state.asin = validationModalInitialState.asin;
        state.service = validationModalInitialState.service;
        state.waitingPassException =
          validationModalInitialState.waitingPassException;
        state.receivedPassException =
          validationModalInitialState.receivedPassException;
        state.correctCatalogDefaultsExpandedVisible =
          validationModalInitialState.correctCatalogDefaultsExpandedVisible;
        state.passExceptionExpandedVisible =
          validationModalInitialState.passExceptionExpandedVisible;
        state.waitingGetCorrectCatalogDefaults =
          validationModalInitialState.waitingGetCorrectCatalogDefaults;
        state.receivedGetCorrectCatalogDefaults =
          validationModalInitialState.receivedGetCorrectCatalogDefaults;
        state.waitingPublishCorrectCatalogDefaults =
          validationModalInitialState.waitingPublishCorrectCatalogDefaults;
        state.receivedPublishCorrectCatalogDefaults =
          validationModalInitialState.receivedPublishCorrectCatalogDefaults;
        state.catalogDefaultsValues =
          validationModalInitialState.catalogDefaultsValues;
        state.catalogDefaultsValuesLookup =
          validationModalInitialState.catalogDefaultsValuesLookup;
        state.submittedPublishCorrectCatalogDefaults =
          validationModalInitialState.submittedPublishCorrectCatalogDefaults;
      }
    },
    setCorrectCatalogDefaults(
      state: ValidationTableState,
      action: PayloadAction<CatalogDefaultValues | undefined>
    ) {
      state.catalogDefaultsValues = action.payload;
    },
    awaitingPassException(state: ValidationTableState) {
      state.waitingPassException = true;
    },
    setCorrectCatalogDefaultsExpandedVisible(state: ValidationTableState) {
      state.correctCatalogDefaultsExpandedVisible = true;
    },
    setCorrectCatalogDefaultsExpandedNotVisible(state: ValidationTableState) {
      state.correctCatalogDefaultsExpandedVisible = false;
    },
    setPassExceptionExpandedVisible(state: ValidationTableState) {
      state.passExceptionExpandedVisible = true;
    },
    setPassExceptionExpandedNotVisible(state: ValidationTableState) {
      state.passExceptionExpandedVisible = false;
    },
    awaitingGetCorrectCatalogDefaults(state: ValidationTableState) {
      state.waitingGetCorrectCatalogDefaults = true;
    },
    awaitingPublishCorrectCatalogDefaults(state: ValidationTableState) {
      state.waitingPublishCorrectCatalogDefaults = true;
    },
    receivedPassException(state: ValidationTableState) {
      state.waitingPassException = false;
      state.receivedPassException = true;
    },
    receivedPublishCorrectCatalogDefaults(state: ValidationTableState) {
      state.waitingPublishCorrectCatalogDefaults = false;
      state.receivedPublishCorrectCatalogDefaults = true;
    },
    receivedGetCorrectCatalogDefaults(state: ValidationTableState) {
      state.waitingGetCorrectCatalogDefaults = false;
      state.receivedGetCorrectCatalogDefaults = true;
    },
    hideTableModal(state: ValidationTableState) {
      state.isOpen = false;
    },
    showTableModal(state: ValidationTableState) {
      state.isOpen = true;
    },
    sortDetailSteps(state: ValidationTableState) {
      state.detailSteps = state.detailSteps.sort((itemA, itemB) =>
        itemA.result.localeCompare(itemB.result)
      );
    },
    filterDetailStepsLVS(state: ValidationTableState) {
      state.detailStepsFailedLVS = state.detailStepsFailedLVS.filter(
        (step) => step.result == "fail"
      );
    },
    setSubmittedPublishCorrectCatalogDefaults(
      state: ValidationTableState,
      action: PayloadAction<ValidationAttributeDetailStep[]>
    ) {
      state.submittedPublishCorrectCatalogDefaults = action.payload;
    },
    setCatalogDefaultsValuesLookup(
      state: ValidationTableState,
      action: PayloadAction<CatalogDefaultValues | undefined>
    ) {
      // TODO: Figure out a cleaner solution
      if (action.payload) {
        if (action.payload.brand) {
          state.catalogDefaultsValuesLookup["BRAND"] = action.payload.brand;
        }
        if (action.payload.brandCode) {
          state.catalogDefaultsValuesLookup["BRAND_CODE"] =
            action.payload.brandCode;
        }
        if (action.payload.deprecatedCanBeGiftwrapped !== undefined) {
          state.catalogDefaultsValuesLookup["CAN_BE_GIFT_WRAPPED"] =
            action.payload.deprecatedCanBeGiftwrapped;
        }
        if (action.payload.deprecatedCanBeGiftmessaged !== undefined) {
          state.catalogDefaultsValuesLookup["CAN_BE_GIFT_MESSAGED"] =
            action.payload.deprecatedCanBeGiftmessaged;
        }
        if (action.payload.displayOnWebsite) {
          state.catalogDefaultsValuesLookup["DISPLAY_ON_WEBSITE"] =
            action.payload.displayOnWebsite;
        }
        if (action.payload.displayAttribute) {
          state.catalogDefaultsValuesLookup["DISPLAY_ATTRIBUTE"] =
            action.payload.displayAttribute;
        }
        if (action.payload.glProductTypeGroupType) {
          state.catalogDefaultsValuesLookup["GL_PRODUCT_GROUP_TYPE"] =
            action.payload.glProductTypeGroupType;
        }
        if (action.payload.itemTypeKeyword) {
          state.catalogDefaultsValuesLookup["ITEM_TYPE_KEYWORD"] =
            action.payload.itemTypeKeyword;
        }
        if (action.payload.manufacturer) {
          state.catalogDefaultsValuesLookup["MANUFACTURER"] =
            action.payload.manufacturer;
        }
        if (action.payload.productCategory) {
          state.catalogDefaultsValuesLookup["PRODUCT_CATEGORY"] =
            action.payload.productCategory;
        }
        if (action.payload.productSubCategory) {
          state.catalogDefaultsValuesLookup["PRODUCT_SUBCATEGORY"] =
            action.payload.productSubCategory;
        }
        if (action.payload.productType) {
          state.catalogDefaultsValuesLookup["PRODUCT_TYPE"] =
            action.payload.productType;
        }
        if (action.payload.recommendedBrowseNodes) {
          state.catalogDefaultsValuesLookup["RECOMMENDED_BROWSE_NODES"] =
            action.payload.recommendedBrowseNodes;
        }
        if (action.payload.sellerOfRecordVendorId) {
          state.catalogDefaultsValuesLookup["SOR_ID"] =
            action.payload.sellerOfRecordVendorId;
        }
        if (action.payload.title) {
          state.catalogDefaultsValuesLookup["TITLE"] = action.payload.title;
        }
        if (action.payload.bulletPoint) {
          state.catalogDefaultsValuesLookup["FEATURE_BULLETS"] =
            action.payload.bulletPoint;
        }
      }
    },
  },
});

export const {
  setValidationModalState,
  awaitingPassException,
  receivedPassException,
  receivedPublishCorrectCatalogDefaults,
  hideTableModal,
  showTableModal,
  awaitingGetCorrectCatalogDefaults,
  receivedGetCorrectCatalogDefaults,
  awaitingPublishCorrectCatalogDefaults,
  setCorrectCatalogDefaults,
  setCatalogDefaultsValuesLookup,
  setCorrectCatalogDefaultsExpandedVisible,
  setCorrectCatalogDefaultsExpandedNotVisible,
  setPassExceptionExpandedVisible,
  setPassExceptionExpandedNotVisible,
  sortDetailSteps,
  filterDetailStepsLVS,
  setSubmittedPublishCorrectCatalogDefaults,
} = validationModalState.actions;

export default validationModalState.reducer;

export function refreshPage() {
  window.location.reload();
}

export const createPassException =
  (
    asin: FullAsinWithAudits | null,
    detailSteps: ValidationAttributeDetailStep[],
    service: string
  ): AppThunk =>
  async (dispatch) => {
    await createAsinMarketplaceValidationDetailPassExceptions(
      asin,
      detailSteps,
      service
    )
      .then(() => refreshPage())
      .catch((reject) => {
        dispatch(
          createNotificationDefinition(
            "error",
            "CreatePassExceptions Error",
            true,
            `${
              reject.response.data.message?.toString() ||
              "CreatePassExceptions did not succeed in DevicesLaunchControlService"
            }` +
              `. \nUse submit a SIM Ticket link at the bottom of Devices ASIN Central if this continues`
          )
        );
        return;
      })
      .finally(() => {
        dispatch(setValidationModalState(validationModalInitialState));
      });
  };

export const createPassExceptionNew =
  (
    asin: FullAsinWithAudits | null,
    detailSteps: ValidationAttributeDetailStep[],
    service: string
  ): AppThunk =>
  async (dispatch) => {
    await createAsinMarketplaceValidationDetailPassExceptions(
      asin,
      detailSteps,
      service
    )
      .then((result) =>
        dispatch(
          createNotificationDefinition(
            "success",
            "Submitted Create Pass Exception",
            true,
            `Submitted Create Pass Exception, please refresh page if validation result does not update`
          )
        )
      )
      .catch((reject) => {
        dispatch(
          createNotificationDefinition(
            "error",
            "CreatePassExceptions Error",
            true,
            `${
              reject.response.data.message?.toString() ||
              "CreatePassExceptions did not succeed in DevicesLaunchControlService"
            }` +
              `. \nUse submit a SIM Ticket link at the bottom of Devices ASIN Central if this continues`
          )
        );
        return;
      })
      .finally(() => {
        dispatch(receivedPassException());
      });
  };

export const publishCorrectCatalogDefault =
  (
    asin: FullAsinWithAudits | null,
    detailSteps: ValidationAttributeDetailStep[]
  ): AppThunk =>
  async (dispatch) => {
    // TODO: Figure out a more efficient solution
    const brand = detailSteps.find(
      (step) => step.step === "BRAND"
    )?.correctedValue;
    const brandCode = detailSteps.find(
      (step) => step.step === "BRAND_CODE"
    )?.correctedValue;
    const deprecatedCanBeGiftmessaged = detailSteps.find(
      (step) => step.step === "CAN_BE_GIFT_MESSAGED"
    )?.correctedValue;
    const deprecatedCanBeGiftwrapped = detailSteps.find(
      (step) => step.step === "CAN_BE_GIFT_WRAPPED"
    )?.correctedValue;
    const displayAttribute = detailSteps.find(
      (step) => step.step === "DISPLAY_ATTRIBUTE"
    )?.correctedValue;
    const displayOnWebsite = detailSteps.find(
      (step) => step.step === "DISPLAY_ON_WEBSITE"
    )?.correctedValue;
    const glProductTypeGroupType = detailSteps.find(
      (step) => step.step === "GL_PRODUCT_GROUP_TYPE"
    )?.correctedValue;
    const itemTypeKeyword = detailSteps.find(
      (step) => step.step === "ITEM_TYPE_KEYWORD"
    )?.correctedValue;
    const manufacturer = detailSteps.find(
      (step) => step.step === "MANUFACTURER"
    )?.correctedValue;
    const productCategory = detailSteps.find(
      (step) => step.step === "PRODUCT_CATEGORY"
    )?.correctedValue;
    const productSubCategory = detailSteps.find(
      (step) => step.step === "PRODUCT_SUBCATEGORY"
    )?.correctedValue;
    const productType = detailSteps.find(
      (step) => step.step === "PRODUCT_TYPE"
    )?.correctedValue;
    const recommendedBrowseNodes = detailSteps.find(
      (step) => step.step === "RECOMMENDED_BROWSE_NODES"
    )?.correctedValue;
    const sellerOfRecordVendorId = detailSteps.find(
      (step) => step.step === "SOR_ID"
    )?.correctedValue;
    const title = detailSteps.find(
      (step) => step.step === "TITLE"
    )?.correctedValue;
    const featureBullets = detailSteps.find(
      (step) => step.step === "FEATURE_BULLETS"
    )?.correctedValue;
    await publishCorrectCatalogDefaults(
      asin,
      brand,
      brandCode,
      deprecatedCanBeGiftmessaged == undefined
        ? undefined
        : Boolean(deprecatedCanBeGiftmessaged),
      deprecatedCanBeGiftwrapped == undefined
        ? undefined
        : Boolean(deprecatedCanBeGiftwrapped),
      displayAttribute,
      displayOnWebsite,
      glProductTypeGroupType,
      itemTypeKeyword,
      manufacturer,
      productCategory,
      productSubCategory,
      productType,
      recommendedBrowseNodes,
      sellerOfRecordVendorId,
      title,
      featureBullets
    )
      .then((result) =>
        dispatch(
          createNotificationDefinition(
            "success",
            "Submitted Correct Catalog Default Values",
            true,
            `Submitted Correct Catalog Default Values successfully with workflow ID ${result.workflowId}. It may take up to 24 hours for values to reconcile ` +
              `\nin Catalog. You must re-run LVS Validation after reconcilation to update the validation results in Devices ASIN Central.`
          )
        )
      )
      .catch((reject) => {
        dispatch(
          createNotificationDefinition(
            "error",
            "PublishCorrectCatalogDefault Error",
            true,
            `${
              reject.response.data.message?.toString() ||
              "PublishCorrectCatalogDefault did not succeed in DevicesLaunchControlService"
            }` +
              `. \nUse submit a SIM Ticket link at the bottom of Devices ASIN Central if this continues`
          )
        );
        return;
      })
      .finally(() => {
        dispatch(receivedPublishCorrectCatalogDefaults());
      });
  };
